// react
import { useEffect, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../../../components/highcharts/themes';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface DataItem {
  year_month: string;
  consumption: number;
}

type YearKey =
  | 'previous_year'
  | 'last_year'
  | 'this_year'
  | 'previous_calendar_year'
  | 'last_calendar_year'
  | 'current_calendar_year';

interface DataShape {
  previous_year?: DataItem[];
  last_year?: DataItem[];
  this_year?: DataItem[];
  previous_calendar_year?: DataItem[];
  last_calendar_year?: DataItem[];
  current_calendar_year?: DataItem[];
}

interface Props {
  data: DataShape;
  isLoading?: boolean;
  unit?: string;
}

const PortfolioAnalysisGroupedColumnChart = ({
  data,
  isLoading,
  unit = 'kWh',
}: Props) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const theme = useTheme();

  const [mode, setMode] = useState('month');
  const [yearMode, setYearMode] = useState('dynamic');

  const dynamicYearConfig: { key: YearKey }[] = [
    { key: 'previous_year' },
    { key: 'last_year' },
    { key: 'this_year' },
  ];
  const calendarYearConfig: { key: YearKey }[] = [
    { key: 'previous_calendar_year' },
    { key: 'last_calendar_year' },
    { key: 'current_calendar_year' },
  ];

  const getYearFromYearMonth = (yearMonth: string) =>
    Number(yearMonth?.split('-')[0]);
  const getMonthFromYearMonth = (yearMonth: string) =>
    Number(yearMonth?.split('-')[1]);

  const buildCalendarYearData = () => {
    const dynamicItems = [
      ...(data?.previous_year || []),
      ...(data?.last_year || []),
      ...(data?.this_year || []),
    ];

    if (dynamicItems.length === 0) {
      return {
        previous_calendar_year: [],
        last_calendar_year: [],
        current_calendar_year: [],
      };
    }

    const itemsByYearMonth = new Map();

    dynamicItems.forEach((item) => {
      if (item?.year_month) {
        itemsByYearMonth.set(item.year_month, item);
      }
    });

    const allItems = Array.from(itemsByYearMonth.values()).sort((left, right) =>
      left.year_month.localeCompare(right.year_month),
    );
    const availableYears = Array.from(
      new Set(allItems.map((item) => getYearFromYearMonth(item.year_month))),
    ).sort((left, right) => left - right);
    const currentCalendarYear = availableYears[availableYears.length - 1];

    const getItemsForYear = (year: number) =>
      allItems.filter((item) => getYearFromYearMonth(item.year_month) === year);

    return {
      previous_calendar_year: getItemsForYear(currentCalendarYear - 2),
      last_calendar_year: getItemsForYear(currentCalendarYear - 1),
      current_calendar_year: getItemsForYear(currentCalendarYear),
    };
  };

  const derivedCalendarYearData = buildCalendarYearData();
  const transformedData = {
    ...data,
    previous_calendar_year:
      data?.previous_calendar_year ||
      derivedCalendarYearData.previous_calendar_year,
    last_calendar_year:
      data?.last_calendar_year || derivedCalendarYearData.last_calendar_year,
    current_calendar_year:
      data?.current_calendar_year ||
      derivedCalendarYearData.current_calendar_year,
  };

  const getSeriesItems = (key: YearKey): DataItem[] =>
    transformedData?.[key] || [];
  const hasDynamicYearData = getSeriesItems('this_year').length > 0;
  const hasCalendarYearData =
    getSeriesItems('current_calendar_year').length > 0;
  const effectiveYearMode = hasCalendarYearData ? yearMode : 'dynamic';
  const selectedYearConfig =
    effectiveYearMode === 'calendar' ? calendarYearConfig : dynamicYearConfig;

  const formatYearMonth = (yearMonth: string | undefined): string => {
    if (!yearMonth) return '';

    const [year, month] = yearMonth.split('-').map(Number);
    const date = new Date(year, month - 1);
    return date.toLocaleString('da-DK', { month: 'short', year: 'numeric' });
  };

  const getCalendarYearLabel = (items: DataItem[]): string => {
    const yearMonth = items?.[0]?.year_month;
    return yearMonth ? yearMonth.split('-')[0] : '';
  };

  const getDynamicRangeLabel = (items: DataItem[]): string => {
    if (!items?.length) return '';

    const firstLabel = formatYearMonth(items[0].year_month);
    const lastLabel = formatYearMonth(items[items.length - 1].year_month);

    return `${firstLabel} - ${lastLabel}`;
  };

  const getSeriesLabel = (items: DataItem[]): string => {
    return effectiveYearMode === 'calendar'
      ? getCalendarYearLabel(items)
      : getDynamicRangeLabel(items);
  };

  const energyUsageLabel = t('common.energy_usage');

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading(t('common.loading_data'));
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading, t]);

  if (!data || (!hasDynamicYearData && !hasCalendarYearData)) {
    return <div>{t('no_data', { ns: 'common' })}</div>;
  }
  const rawChartSeries = selectedYearConfig.map(({ key }) => {
    const items = getSeriesItems(key);

    return {
      items,
      name: getSeriesLabel(items),
      totals: items.map((item) => item.consumption),
    };
  });

  const calendarMonthNumbers = Array.from(
    new Set(
      rawChartSeries.flatMap((series) =>
        series.items.map((item) => getMonthFromYearMonth(item.year_month)),
      ),
    ),
  ).sort((left, right) => left - right);

  const months =
    effectiveYearMode === 'calendar'
      ? calendarMonthNumbers.map((monthNumber) =>
          new Date(2000, monthNumber - 1).toLocaleString('da-DK', {
            month: 'short',
          }),
        )
      : rawChartSeries[rawChartSeries.length - 1]?.items.map((item) =>
          formatYearMonth(item.year_month),
        ) || [];

  const chartSeries = rawChartSeries.map((series) => {
    if (effectiveYearMode !== 'calendar') {
      return {
        ...series,
        data: series.items.map((item) => item.consumption),
        pointLabels: series.items.map((item) =>
          formatYearMonth(item.year_month),
        ),
      };
    }

    const itemsByMonth = new Map(
      series.items.map((item) => [
        getMonthFromYearMonth(item.year_month),
        item,
      ]),
    );

    return {
      ...series,
      data: calendarMonthNumbers.map(
        (monthNumber) => itemsByMonth.get(monthNumber)?.consumption ?? null,
      ),
      pointLabels: calendarMonthNumbers.map((monthNumber) => {
        const item = itemsByMonth.get(monthNumber);
        return item ? formatYearMonth(item.year_month) : '';
      }),
    };
  });

  // Helper to sum arrays (handles numbers or numeric strings)
  const sum = (arr: number[]): number =>
    (arr || []).reduce((acc: number, v: number) => acc + (Number(v) || 0), 0);

  const summaryCategories = chartSeries.map((series) => series.name);
  const summaryData = rawChartSeries.map((series) => sum(series.totals));

  // Build options depending on mode
  const options =
    mode === 'month'
      ? {
          chart: {
            type: 'column',
          },
          title: {
            text: ``,
          },
          xAxis: {
            categories: months, // Use extracted months as categories
            labels: {
              formatter: function () {
                const val = String(
                  (
                    this as unknown as Highcharts.AxisLabelsFormatterContextObject
                  ).value,
                );
                if (val.includes('maj')) return val.split(' ')[0];
                else return val.split('.')[0];
              },
            },
            title: {
              text: t('common.month'),
            },
          },
          plotOptions: {
            column: {
              // maximum pixel width of each column — prevents columns from becoming tiny and spaced far apart
              maxPointWidth: 0,
              // reduce padding between columns in the single category
              groupPadding: 0.1,
              pointPadding: 0.1,
            },
            series: {
              // space the series slightly so they don't overlap; you can tweak this
              pointPlacement: 'between',
            },
          },
          yAxis: {
            min: 0,
            title: {
              text: `Total [${unit}]`,
            },
          },
          series: chartSeries.map((series) => ({
            name: series.name,
            data: series.data,
            tooltip: {
              headerFormat: '',
              valueDecimals: 0,
              valueSuffix: ` ${unit}`,
              pointFormatter: function (): string {
                const point = this as unknown as Highcharts.Point & {
                  index: number;
                };
                const pointLabel =
                  series.pointLabels[point.index] || point.category;

                return `<span>${pointLabel}</span><br/><span>${energyUsageLabel}</span>: <b>${formatNumber(
                  point.y ?? 0,
                )} ${unit}</b><br/>`;
              },
            },
          })),
        }
      : {
          // summary 3-bar mode — render the three summary bars inside a single category
          // so they stay close together (like flex space-around).
          chart: {
            type: 'column',
          },
          title: {
            text: ``,
          },
          // single category so series sit next to each other instead of being spread across the axis
          xAxis: {
            categories: [t('common.year')],
            title: { text: '' },
            labels: { enabled: false },
          },
          yAxis: {
            min: 0,
            title: { text: `Total [${unit}]` },
          },
          // Keep the three summary columns visually close even on wide screens
          plotOptions: {
            column: {
              // maximum pixel width of each column — prevents columns from becoming tiny and spaced far apart
              maxPointWidth: 160,
              // reduce padding between columns in the single category
              groupPadding: 0.3,
              pointPadding: 0.1,
            },
            series: {
              // space the series slightly so they don't overlap; you can tweak this
              pointPlacement: 'between',
            },
          },
          // Build series so each has a single-point array (placed in the single category)
          series: summaryData.map((val, idx) => ({
            name: summaryCategories[idx],
            data: [val],
            tooltip: {
              headerFormat: '',
              valueDecimals: 0,
              valueSuffix: ` ${unit}`,
              pointFormatter: function (): string {
                const point = this as unknown as Highcharts.Point;
                return `<span>${summaryCategories[idx]}</span><br/><span>${energyUsageLabel}</span>: <b>${formatNumber(
                  point.y ?? 0,
                )} ${unit}</b><br/>`;
              },
            },
            // show the name under the column via dataLabels if desired (disabled by default)
            dataLabels: {
              enabled: false,
            },
          })),
        };

  return (
    <div style={{ width: '100%', marginTop: 2 }}>
      {/* Toggle (MUI) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 8,
          alignItems: 'center',
        }}
      >
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(e, value) => {
            if (value) setMode(value);
          }}
          size="small"
          aria-label={t('common.show')}
        >
          <ToggleButton value="month" aria-label={t('common.month')}>
            {t('common.month')}
          </ToggleButton>
          <ToggleButton
            sx={{ px: 2 }}
            value="year"
            aria-label={t('common.year')}
          >
            {t('common.year')}
          </ToggleButton>
        </ToggleButtonGroup>

        {hasCalendarYearData && (
          <ToggleButtonGroup
            value={effectiveYearMode}
            exclusive
            onChange={(e, value) => {
              if (value) setYearMode(value);
            }}
            size="small"
            aria-label={t('common.year')}
          >
            <ToggleButton
              value="dynamic"
              aria-label={t('common.dynamic_years')}
            >
              {t('common.dynamic_years')}
            </ToggleButton>
            <ToggleButton
              value="calendar"
              aria-label={t('common.calendar_year')}
            >
              {t('common.calendar_year')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </div>

      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </div>
  );
};

export default PortfolioAnalysisGroupedColumnChart;
